<template>
	<CModal
		:show.sync="isShow"
		:close-on-backdrop="false"
		centered
		class="modal-branch"
	>
		<template #header-wrapper>
			<ModalHeaderWrapper
				:title="title"
				@onClose="handleCancel"
			/>
		</template>
		<BaseLoading v-if="isLoading" />
		<div v-else>
			<div>
				<CInput
					v-model.trim="keyword"
					placeholder="Search by name"
					class="flex-fill"
					autocomplete="off"
				>
					<template #prepend-content>
						<FontAwesomeIcon :icon="['far', 'search']" />
					</template>
				</CInput>
			</div>
			<slot
				v-if="!filteredList.length"
				name="no-item"
			>
			</slot>
			<div v-show="filteredList.length" class="list">
				<div class="select-all mb-1 align-items-center">
					<CInputCheckbox
						:id="`select-all-branch_${uid}`"
						:checked.sync="isSelectedAll"
						custom
						label="Select all"
						class="select-all-input"
						add-label-classes="font-weight-bold"
						data-test-id="select-all-input"
						@update:checked="handleCheckedSelectAll"
					>
						<template #label>
							<label :for="`select-all-branch_${uid}`" class="custom-control-label list-label w-100 typo-body-2 font-weight-normal text-nowrap text-truncate color-black-45 mb-0 ml-2">
								<span class="list-cutomer-group color-black font-weight-bold">Select all</span>
							</label>
						</template>
					</CInputCheckbox>
				</div>
				<DynamicScroller
					:items="filteredList"
					:min-item-size="35"
					key-field="id"
					class="scroller list-select"
				>
					<template v-slot="{ item, index, active }">
						<DynamicScrollerItem
							:item="item"
							:active="active"
							:data-index="index"
							class="list-item"
						>
							<div class="d-flex flex-fill">
								<CInputCheckbox
									:id="`${uid}_${item.id}`"
									:checked="checkItemIsSelected(item.id)"
									custom
									class="list-checkbox overflow-hidden"
									@update:checked="handleCheckedBranch($event, item)"
								>
									<template #label>
										<label :for="`${uid}_${item.id}`" class="custom-control-label list-label w-100 typo-body-2 font-weight-normal text-nowrap text-truncate color-black-45 mb-0 ml-2">
											<span class="list-cutomer-group color-black">{{ item.name }}</span>
										</label>
									</template>
								</CInputCheckbox>
							</div>
						</DynamicScrollerItem>
					</template>
				</DynamicScroller>
			</div>
		</div>
		<template #footer>
			<BaseModalFooter
				:total-select="totalSelect"
				:disabled-comfirm="isSubmitting"
				:disabled-cancel="isSubmitting"
				:disabled-clear="isSubmitting"
				@onClear="handleClear"
				@onCancel="handleCancel"
				@onConfirm="handleSubmit"
			/>
		</template>
	</CModal>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { DynamicScroller, DynamicScrollerItem } from 'vue-virtual-scroller';
import uniqueId from 'lodash/uniqueId';
import 'vue-virtual-scroller/dist/vue-virtual-scroller.css';
import { cloneDeep } from '../helpers/utils';

export default {
	components: {
		DynamicScroller,
		DynamicScrollerItem,
	},
	props: {
		title: {
			type: String,
			default: null,
		},
		selectedBranches: {
			type: Array,
			default: () => [],
		},
	},

	data() {
		return {
			uid: null,
			isShow: false,
			items: [],
			isSelectedAll: false,
			keyword: '',
			localBranches: [...this.selectedBranches],
		};
	},

	computed: {
		...mapState('branchList', {
			branches: 'data',
			isLoading: 'isLoading',
		}),
		filteredList() {
			const keyword = this.keyword.toLowerCase();

			if (!this.keyword) {
				return this.items;
			}

			const isIncludeId = (item) => item.id.toString().includes(keyword);
			const isIncludeName = (item) => item.name.toLowerCase().includes(keyword);
			return this.items.filter((item) => isIncludeId(item) || isIncludeName(item));
		},
		isSubmitting() {
			return this.isLoading;
		},
		totalSelect() {
			return this.localBranches.length;
		},
	},
	watch: {
		filteredList() {
			this.checkSelectionState();
		},
		localBranches() {
			this.checkSelectionState();
		},
	},
	mounted() {
		this.uid = uniqueId('branch_id');
	},
	methods: {
		...mapActions('branchList', {
			getBranches: 'getBranches',
		}),
		async fetchData({ keyword }) {
			await this.getBranches({
				keyword,
				per_page: 'all',
			});

			this.items = this.branches.map((branch) => {
				return {
					id: branch.id,
					name: `ID#${branch.itecBranchId} ${branch.nameTH}`,
					nameTH: branch.nameTH,
					nameEN: branch.nameEN,
				};
			});
		},
		open() {
			this.isShow = true;
			this.keyword = '';
			this.fetchData({ keyword: this.keyword });
			this.resetState();
		},
		close() {
			this.resetState();
			this.isShow = false;
		},
		handleClear() {
			this.localBranches = [];
		},
		handleSubmit() {
			this.$emit('onSubmit', this.localBranches);
			this.isShow = false;
		},
		handleCancel() {
			this.close();
		},
		onInputEnter(event) {
			this.fetchData({ keyword: event.target.value });
		},
		setSelectedAllState(state) {
			let isChecked = false;
			let isIndeterminate = false;
			switch (state) {
				case 'checked': {
					isChecked = true;
					isIndeterminate = false;
					break;
				}
				case 'unchecked': {
					isChecked = false;
					isIndeterminate = false;
					break;
				}
				case 'indeterminated': {
					isChecked = true;
					isIndeterminate = true;
					break;
				}
				default: break;
			}

			const selectAllEle = this.$el.querySelector('.select-all-input input');
			if (selectAllEle) {
				selectAllEle.checked = isChecked;
				selectAllEle.indeterminate = isIndeterminate;
			}
		},
		handleCheckedSelectAll() {
			if (this.isSelectedAll) {
				const selectedItems = this.filteredList;
				this.addToSelectedList(selectedItems);
			} else {
				const selectedItems = this.filteredList.map((item) => item.id);
				this.removeFromSelectedList(selectedItems);
			}
		},
		checkItemIsSelected(val) {
			return this.localBranches.some((item) => item.id === val);
		},
		checkSelectionState() {
			const isEverySelected = this.filteredList.length && this.filteredList.every((group) => this.localBranches.map((item) => item.id).includes(group.id));
			const isSomeSelected = this.filteredList.length && this.filteredList.some((group) => this.localBranches.map((item) => item.id).includes(group.id));

			if (isEverySelected) {
				this.setSelectedAllState('checked');
				return;
			}

			if (isSomeSelected) {
				this.setSelectedAllState('indeterminated');
				return;
			}

			this.setSelectedAllState('unchecked');
		},
		handleCheckedBranch(checked, group) {
			const isSelected = this.localBranches.some((item) => item.id === group.id);
			if (!isSelected) {
				this.addToSelectedList(group);
			} else {
				this.removeFromSelectedList(group.id);
			}
		},
		addToSelectedList(items = []) {
			let newItems = items;
			if (!Array.isArray(items)) {
				newItems = [items];
			}
			this.localBranches = [...new Set([...this.localBranches, ...newItems])].sort();
		},
		removeFromSelectedList(items = []) {
			let itemsToRemove = items;
			if (!Array.isArray(items)) {
				itemsToRemove = [items];
			}

			this.localBranches = this.localBranches.filter((selectedItem) => !itemsToRemove.includes(selectedItem.id));
		},
		resetState() {
			this.localBranches = cloneDeep(this.selectedBranches);
		},
	},
};
</script>

<style lang="scss" scoped>
	.clear-button {
		cursor: pointer;
	}

	.list {
		margin: 0 -#{rem(16)} -#{rem(16)};
	}

	.select-all {
		background-color: $color-gray-100;
		padding: rem(8) rem(16);
	}

	.list-select {
		min-height: rem(360);
		max-height: rem(360);
		list-style: none;
		padding: 0;
		margin: 0;

		.list-item {
			padding: rem(6) rem(16);

			label {
				margin-top: rem(3);
				cursor: pointer;
			}
		}
	}
</style>
